import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { submitToSheet } from '../services/googleSheetService';
import { PROPERTY_TYPES, PRICE_OPTIONS, DISTRICT_KEYS, AMENITIES_LIST } from '../constants';

const initialFormData = {
  name: '',
  phone: '',
  propertyType: '',
  district: '',
  minPrice: '',
  maxPrice: '',
  amenities: [],
  moveInDate: '',
  note: '',
};

const FindByRequest = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ ...initialFormData });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData((prev) => {
      const amenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities };
    });
  };

  const formatPrice = (value) => {
    if (!value) return '';
    const num = parseInt(value);
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' triệu';
    }
    return num.toLocaleString('vi-VN') + '₫';
  };

  // Direct Vietnamese name mapping for districts (most reliable, no i18n dependency)
  const DISTRICT_VIETNAMESE = {
    district1: 'Quận 1',
    district2: 'Quận 2',
    district3: 'Quận 3',
    district4: 'Quận 4',
    district5: 'Quận 5',
    district6: 'Quận 6',
    district7: 'Quận 7',
    district8: 'Quận 8',
    district9: 'Quận 9',
    district10: 'Quận 10',
    district11: 'Quận 11',
    district12: 'Quận 12',
    districtBinhThanh: 'Bình Thạnh',
    districtThuDuc: 'Thủ Đức',
    districtGoVap: 'Gò Vấp',
    districtTanBinh: 'Tân Bình',
    districtTanPhu: 'Tân Phú',
    districtPhuNhuan: 'Phú Nhuận',
  };

  const resolveVietnameseValue = (key) => {
    if (!key) return '';

    // 1. Direct district lookup (most reliable, no i18n dependency)
    if (DISTRICT_VIETNAMESE[key]) return DISTRICT_VIETNAMESE[key];

    // 2. Try as amenity via i18n
    const amenityKey = `amenity.${key}`;
    const amenityResult = t(amenityKey, { lng: 'vi' });
    if (amenityResult !== amenityKey) return amenityResult;

    // 3. Try as district via i18n (fallback in case DISTRICT_VIETNAMESE is missing a key)
    const districtKey = `about.form.${key}`;
    const districtResult = t(districtKey, { lng: 'vi' });
    if (districtResult !== districtKey) return districtResult;

    // 4. Last resort: return the original key
    return key;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Build submission data with Vietnamese display values + phone as string
      // Note: formData.propertyType already stores the Vietnamese value (e.g. 'Ký túc xá', 'Phòng (1 - 2 người)')
      // so we pass it directly. Districts and amenities are i18n keys that need resolution.
      const submissionData = {
        name: formData.name,
        phone: String(formData.phone),
        propertyType: formData.propertyType,
        district: resolveVietnameseValue(formData.district),
        minPrice: formData.minPrice ? formatPrice(formData.minPrice) : '',
        maxPrice: formData.maxPrice ? formatPrice(formData.maxPrice) : '',
        amenities: formData.amenities.map((a) => resolveVietnameseValue(a)),
        moveInDate: formData.moveInDate,
        note: formData.note,
      };

      await submitToSheet(submissionData);
      setShowSuccess(true);
      setFormData({ ...initialFormData });
    } catch (err) {
      setError(err.message || t('about.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      titleKey: 'features.verified.title',
      descKey: 'features.verified.desc',
      color: 'bg-primary-100 text-primary-600',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      titleKey: 'features.direct.title',
      descKey: 'features.direct.desc',
      color: 'bg-house-100 text-house-600',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      titleKey: 'features.transparent.title',
      descKey: 'features.transparent.desc',
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      titleKey: 'features.support.title',
      descKey: 'features.support.desc',
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{t('about.title')}</h1>
          <p className="text-lg md:text-xl text-primary-200 max-w-2xl mx-auto">{t('about.subtitle')}</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="request-form">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-10">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">{t('about.form.title')}</h2>
          <p className="text-gray-500 text-center mb-8">{t('about.missionDesc')}</p>

          {showSuccess ? (
            <div className="text-center py-10">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('about.form.successTitle')}</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">{t('about.form.success')}</p>
              <button
                onClick={() => setShowSuccess(false)}
                className="px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
              >
                {t('about.form.close')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row: Name + Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('about.form.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('about.form.namePlaceholder')}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('about.form.phone')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('about.form.phonePlaceholder')}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Row: Property Type + District */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('about.form.propertyType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors bg-white"
                  >
                    <option value="">{t('about.form.propertyTypePlaceholder')}</option>
                    {PROPERTY_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{t(type.labelKey)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('about.form.district')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors bg-white"
                  >
                    <option value="">{t('about.form.districtPlaceholder')}</option>
                    {DISTRICT_KEYS.map((d) => (
                      <option key={d} value={d}>{t(`about.form.${d}`)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('about.form.priceRange')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    name="minPrice"
                    value={formData.minPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors bg-white"
                  >
                    {PRICE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.labelKey ? t(opt.labelKey) : opt.label}
                      </option>
                    ))}
                  </select>
                  <select
                    name="maxPrice"
                    value={formData.maxPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors bg-white"
                  >
                    {PRICE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.labelKey ? t(opt.labelKey) : opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t('about.form.amenities')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {AMENITIES_LIST.map((amenity) => {
                    const isSelected = formData.amenities.includes(amenity.key);
                    return (
                      <button
                        key={amenity.key}
                        type="button"
                        onClick={() => handleAmenityToggle(amenity.key)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg">{amenity.icon}</span>
                        <span>{t(`amenity.${amenity.key}`)}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 ml-auto text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Move-in Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('about.form.moveInDate')}
                </label>
                <input
                  type="date"
                  name="moveInDate"
                  value={formData.moveInDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('about.form.note')}
                </label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  placeholder={t('about.form.notePlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors resize-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-primary-600 text-white rounded-xl font-bold text-lg hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('about.form.submitting')}
                  </>
                ) : (
                  t('about.form.submit')
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default FindByRequest;